/**
 * @core-loop/ui — Header component
 *
 * Shared platform header for all core-loop.dev apps.
 * Renders the site brand, platform app nav, auth state, and theme toggle.
 *
 * Props:
 *   appName        {string}   Current app display name (e.g. "Finance", "Social")
 *   appHref        {string}   Current app root URL (e.g. "https://finance.core-loop.dev")
 *   locale         {string}   Current locale code (e.g. "en")
 *   theme          {string}   "dark" | "light"
 *   onToggleTheme  {Function} Callback to toggle theme
 *   user           {object}   null if unauthenticated; { email, roles } if authenticated
 *   onLogout       {Function} Callback for logout action
 *   publicApps     {Array}    Optional override for the platform app links list
 *                             Default: the canonical PUBLIC_APPS list
 *
 * HC-SHARED-UI: This is the single authoritative Header implementation.
 * HC-PRIVATE-APP: Private apps must not appear in publicApps.
 */

import type { ReactNode } from 'react';

export interface PlatformApp { name: string; href: string; }
export interface HeaderUser { email: string; roles?: string[]; }

export const PUBLIC_APPS: PlatformApp[] = [
  { name: 'Finance', href: 'https://finance.core-loop.dev' },
  { name: 'Social', href: 'https://social.core-loop.dev' },
  { name: 'Blog', href: 'https://blog.core-loop.dev' },
  { name: 'Media Cleaner', href: 'https://clean.core-loop.dev' },
];

interface HeaderProps {
  appName?: string;
  appHref?: string;
  locale?: string;
  theme?: string;
  onToggleTheme?: () => void;
  user?: HeaderUser | null;
  onLogout?: () => void;
  onLogin?: () => void;
  loginHref?: string;
  publicApps?: PlatformApp[];
  /** Optional extra content rendered inside the actions bar (e.g. language picker) */
  extraActions?: ReactNode;
  /** Set true when the consuming app manages its own auth UI via extraActions.
   *  Suppresses BOTH the sign-in button AND the user email/sign-out section. */
  hideAuth?: boolean;
  /**
   * Auth API base URL — used to fire a warm-up ping on first mount so the
   * Function App is ready by the time the user triggers a sign-in.
   * Defaults to the production auth endpoint.
   */
  authUrl?: string;
  variant?: 'app' | 'site';
}

export function Header({
  appName,
  appHref,
  locale = 'en',
  theme = 'dark',
  onToggleTheme,
  user = null,
  onLogout,
  onLogin,
  loginHref,
  publicApps = PUBLIC_APPS,
  extraActions,
  hideAuth = false,
  variant = 'app',
  authUrl = 'https://auth.core-loop.dev',
}: HeaderProps) {
  void authUrl;
  const homeHref = `https://core-loop.dev${locale !== 'en' ? `/${locale}` : ''}`;
  const headerClass = variant === 'site' ? 'cl-header cl-header--site' : 'cl-header';

  return (
    <header className={headerClass} aria-label="Site header">
      <a href="#main-content" className="cl-skip-link">Skip to content</a>

      <div className="cl-header__brand">
        <a href={homeHref} className="cl-header__logo" aria-label="core-loop home">
          core-loop
        </a>
        {appName && (
          <>
            <span className="cl-header__separator" aria-hidden="true">/</span>
            <a href={appHref} className="cl-header__app-name">{appName}</a>
          </>
        )}
      </div>

      <nav className="cl-header__nav" aria-label="Platform apps">
        {publicApps.map(({ name, href }) => (
          <a
            key={href}
            href={href}
            className={`cl-header__nav-link${appHref === href ? ' cl-header__nav-link--active' : ''}`}
          >
            {name}
          </a>
        ))}
      </nav>

      <div className="cl-header__actions">
        {extraActions}
        {onToggleTheme && (
          <button
            type="button"
            className="cl-theme-toggle"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>
        )}

        {user && !hideAuth ? (
          <div className="cl-header__user">
            <span className="cl-header__email">{user.email}</span>
            {onLogout && (
              <button type="button" className="cl-header__logout" onClick={onLogout}>
                Sign out
              </button>
            )}
          </div>
        ) : !hideAuth && !user && (onLogin ? (
          <button type="button" className="cl-header__login" onClick={onLogin}>
            Sign in
          </button>
        ) : (
          <a href={loginHref ?? '#'} className="cl-header__login">
            Sign in
          </a>
        ))}

      </div>
    </header>
  );
}
